import { store } from 'store/index'
import { Position } from 'store/modules/positions'
import { Skill } from 'store/modules/skills'
import { z } from 'zod'
import { zfd } from 'zod-form-data'
import {
  CheckinCreateError,
  userMessageFromErrors,
} from './checkin-create-error'

export class CheckinFormVm {
  constructor(
    private selectedPositionId: string | undefined | null,
    private source: string,
    private forUserId?: string,
  ) {}

  // The user for whom the check-in is being created.
  // When a manager starts a check-in for an IC, this is the IC.
  // For self-initiated check-ins this is the current user.
  private get subjectUser() {
    if (this.forUserId) return store.users.byId(this.forUserId)
    return store.currentUser
  }

  async onSubmit(e: React.FormEvent<HTMLFormElement>, jointReview = false) {
    if (!store.currentUser) return
    e.preventDefault()

    const formData = new FormData(e.currentTarget)

    const schema = zfd.formData({
      title: zfd.text(z.string().optional()),
      description: zfd.text(z.string().optional()),
      position: zfd.text(
        z
          .string()
          .min(1)
          .default(this.selectedPositionId || ''),
      ),
      assessor: zfd.text(z.string().min(1)),
      skill_ids: z.preprocess(
        (val) => (typeof val === 'string' ? [val] : val),
        z.array(zfd.text()).optional(),
      ),
    })

    const { title, description, position, assessor, skill_ids } =
      schema.parse(formData)
    const selected = store.positions.byId(position)
    if (!selected || (selected.requirementsCount ?? 0) === 0) {
      throw new Error('position-has-no-skills')
    }

    const createProps = {
      title: title,
      description,
      position,
      assessor,
      author: this.subjectUser?.id ?? store.currentUser.id,
      initiated_by: (this.forUserId ? 'assessor' : 'self') as
        'assessor' | 'self',
      joint_review: this.jointReviewRequired ? true : jointReview,
      skill_ids,
      source: this.source,
    }

    const result = await store.checkins.create(createProps)

    // The store swallows request failures (makeRequest reports them and returns
    // `{ success: false }`), so without this a rejected create returned undefined
    // and the modal sat there doing nothing.
    if (!result.success) {
      throw new CheckinCreateError(userMessageFromErrors(result.errors))
    }

    const id = result.data ? Object.keys(result.data.checkins)[0] : undefined
    const checkin = store.checkins.byId(id)

    // A 201 whose payload didn't land in the store leaves nothing to navigate to.
    // Rare, but it fails exactly as silently, so it gets the same treatment.
    if (!checkin) throw new CheckinCreateError()

    return checkin
  }

  get canCheckinAgainstAnyPosition() {
    return !!store.currentUser?.org?.canCheckinAgainstAnyPosition
  }

  get defaultAssessorOption(): string {
    // Manager path: pre-fill the manager (currentUser) as assessor for the IC's check-in.
    if (this.forUserId) return store.currentUser?.id || ''

    const managerId = store.currentUser?.manager?.id
    // Guard against self-referential manager: if the manager is the current user,
    // they are excluded from assessorOptions so we must not pre-select them.
    if (managerId && managerId === store.currentUser?.id) return ''
    return managerId || ''
  }

  get assessorOptions() {
    if (!store.currentUser) return []
    // Exclude the subject user (the IC) from being selectable as their own assessor.
    const excludeId = this.subjectUser?.id ?? store.currentUser.id
    return store.users.withPackage('grow').filter((u) => u.id !== excludeId)
  }

  get finalisedCheckinsCount(): number {
    const userId = this.subjectUser?.id || store.currentUser?.id
    return userId ? store.checkins.finalisedForAuthor(userId).length : 0
  }

  get unfinishedCheckinsCount(): number {
    const userId = this.subjectUser?.id || store.currentUser?.id
    if (!userId) return 0
    return store.checkins.notFinalisedForAuthor(userId).length
  }

  get defaultPositionId(): string {
    return this.subjectUser?.position?.id || ''
  }

  get skills(): Skill[] {
    if (!this.selectedPosition) return []
    return this.selectedPosition.skills.filter((s): s is Skill => !!s)
  }

  get selectedPosition() {
    if (this.positions.length === 0) return
    if (!this.selectedPositionId) return

    return this.positions.find(
      (position) => position.id === this.selectedPositionId,
    )
  }

  get focusSkillIds(): string[] {
    if (!store.currentUser) return []
    return store.userSkills.focusedSkillIdsForUser(store.currentUser.id)
  }

  get positions(): Position[] {
    if (this.canCheckinAgainstAnyPosition) return store.positions.forOrg
    if (this.subjectUser?.position) return [this.subjectUser.position]

    return []
  }

  get disabled() {
    return !this.positions.length
  }

  /**
   * True when the org has joint_review_only configured AND the subject user's team
   * has joint review enabled with "optional" requirement.
   * In this case the check-in start form shows a "Start Joint Review" toggle.
   */
  get showJointReviewToggle(): boolean {
    const org = store.currentUser?.org
    const team = this.subjectUser?.team
    return !!(
      org?.checkinStartType === 'joint_review_only' &&
      team?.jointReviewEnabled &&
      team?.jointReviewRequirement === 'optional'
    )
  }

  /**
   * True when the org has joint_review_only configured AND the subject user's team
   * has joint review enabled with "required" requirement.
   * In this case joint review is always sent as true (no toggle shown to the user).
   */
  get jointReviewRequired(): boolean {
    const org = store.currentUser?.org
    const team = this.subjectUser?.team
    return !!(
      org?.checkinStartType === 'joint_review_only' &&
      team?.jointReviewEnabled &&
      team?.jointReviewRequirement === 'required'
    )
  }
}
